import { useState, useCallback } from 'react';
import { GoldenSourceHelper } from "../../app/Switch_Core_React_Standards";
import {
    fetchCorporateRegistry,
    registerCorporateEntity,
    fetchCorporateHierarchy
} from './corporateInteractionService';

export const useCorporateDetailsController = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const siteKey = process.env.REACT_APP_SITE_KEY;

    /**
     * 🎯 GET REGISTRY
     * Fetches the flat list of all corporate entities.
     */
    const getRegistry = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchCorporateRegistry({ siteKey });
            return data;
        } catch (err) {
            console.error("❌ CONTROLLER ERROR (Registry):", err.message);
            setError(err.message);
            return [];
        } finally {
            setLoading(false);
        }
    }, [siteKey]);

    /**
     * 🎯 GET TREE
     * Fetches the hierarchy starting from a specific root ID.
     */
    const getTree = useCallback(async (corporateEntityId) => {
        if (!corporateEntityId) return [];
        setLoading(true);
        try {
            const data = await fetchCorporateHierarchy(corporateEntityId, { siteKey });
            return data;
        } catch (err) {
            console.error("❌ CONTROLLER ERROR (Tree):", err.message);
            setError(err.message);
            return [];
        } finally {
            setLoading(false);
        }
    }, [siteKey]);

    /**
     * 🎯 SAVE ENTITY
     * Standard registration for new entities.
     */
    const saveEntity = useCallback(async (entityData) => {
        setLoading(true);
        try {
            const result = await registerCorporateEntity(entityData, { siteKey });
            return result;
        } catch (err) {
            console.error("❌ CONTROLLER ERROR (Save):", err.message);
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, [siteKey]);

    /**
     * 🎯 UPDATE HIERARCHY
     * Specific orchestration for linking child entities to parents.
     * Extracts existing data to prevent duplicate record creation.
     */
    const updateHierarchy = useCallback(async (entity, parentId) => {
        setLoading(true);
        try {
            const entityId = GoldenSourceHelper.get(entity, "CorporateEntityId");

            const payload = {
                ...entity,
                CorporateEntityId: entityId,
                ParentEntityId: parentId || null
            };

            const result = await registerCorporateEntity(payload, { siteKey });
            return result;
        } catch (err) {
            console.error("❌ CONTROLLER ERROR (Hierarchy Update):", err.message);
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, [siteKey]);

    // Single return statement containing all methods and state
    return { 
        getRegistry, 
        getTree, 
        saveEntity, 
        updateHierarchy, 
        loading, 
        error 
    };
};